import React, { useState, useEffect, useCallback } from 'react';
import { Button, CircularProgress, Container, Grid, Tooltip, withTheme } from '@material-ui/core';
import { useHistory, useParams } from 'react-router';
import { Alert } from '@material-ui/lab';
import { Form, Formik } from 'formik';
import { ErrorIcon } from '../../icons';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';
import BookingItemGenericSettings from './components/BookingItemGenericSettings';
import BookingItemCompanySettings from './components/BookingItemCompanySettings';
import BookingItemGuestSettings from './components/BookingItemGuestSettings';
import BookingItemTicketSettings from './components/BookingItemTicketSettings';
import BookingItemCustomBookingSettings from './components/BookingItemCustomBookingSettings';
import BookingItemTransactionHistory from './components/BookingItemTransactionHistory';
import BookingItemTimeslotSettings from './components/BookingItemTimeslotSettings.js';
import { RouterPrompt } from '../../components/atom/RouterPrompt';
import { useNotify } from '../../hooks/useNotify';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { useBookingPermissions } from '../../hooks/useBookingPermissions';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { loginAsCustomerWithId } from '../../actions/user';
import { buildUpdateData, updateToSimpleText } from './updateMapper';
import { InternalTicketMergingLogic } from './components/InternalTicketMergingLogic.js';
import BookingItemHistory from './components/BookingItemHistory.js';
import AgentNotesSection from './components/AgentNotesSection';
import DetailsToggleButton from './components/DetailsToggleButton.js';
import BookingItemEmails from './components/BookingItemEmails.js';

function useBecomeThatCustomer(booking, customer, showModal, notifyError) {
    const dispatch = useDispatch();
    useEffect(() => {
        const bookingUserId = booking?.booking?.user?.id;
        if (!bookingUserId) {
            return;
        }
        if (customer && customer.id === bookingUserId) {
            return;
        }
        dispatch(
            loginAsCustomerWithId(
                bookingUserId,
                showModal,
                errorMessage => {
                    if (errorMessage) {
                        notifyError(errorMessage);
                    }
                },
                null
            )
        );
    }, [booking?.booking?.user?.id, customer?.id, dispatch, showModal, notifyError, customer]);
}

const BookingItemEditPage = withTheme(({ theme }) => {
    const history = useHistory();
    const { id } = useParams();
    const { notifyError, notifySuccess } = useNotify();
    const { showModal } = useModals();
    const { customer, user } = useSelector(state => state.user);

    const [booking, setBooking] = useState(null);
    const [ticketData, setTicketData] = useState(null);
    const [error, setError] = useState(true);
    const [loading, setLoading] = useState(true);

    const [visibleDetails, setVisibleDetails] = useState({
        activityInfoShown: false,
        transactionShown: false,
        historyShown: false,
        emailsShown: false
    });

    // booking_item_tickets are stored to surface BUY times
    const [bookingItemTickets, setBookingItemTickets] = useState([]);

    // ticket count changes
    const [forcePaymentMethod, setForcePaymentMethod] = useState('none');

    useBecomeThatCustomer(booking, customer, showModal, notifyError);

    const loadBookingItem = useCallback(
        (id = null) => {
            setLoading(true);
            const params = { bookingItemId: id };
            if (forcePaymentMethod && forcePaymentMethod !== 'none') {
                params.payment_method = forcePaymentMethod;
            }

            doSwaggerCall('bookings', 'getBookingItemById', params)
                .then(data => {
                    setError(false);

                    // store the tickets separately, we don't want to keep peaking into them
                    setTicketData({
                        possibleTickets: data.possibleTickets,
                        existingTickets: data.tickets
                    });
                    setBooking({
                        ...data,
                        // Copy of the current start for comparison/display —
                        // "current", not the historical predefined slot, so
                        // once a custom booking is linked this must be the
                        // custom booking's own time (data.activity_start
                        // stays the original predefined slot forever, same
                        // precedence rule as fullwidget's
                        // resolveActivityStart/bookingsColumns.tsx). Left as
                        // the raw field name for the rest of this page/form,
                        // which never reads it for anything but display and
                        // the "Keep original start time" revert value.
                        original_activity_start:
                            data.custom_booking_id != null
                                ? data.custom_booking?.activity_start || data.activity_start
                                : data.activity_start,
                        // remove ticket references here, should have already mapped
                        // making sure we do not peak into them
                        possibleTickets: undefined,
                        tickets: undefined,
                        booking_item_tickets: undefined,
                        // place to new set of tickets
                        uniqueTickets: [],
                        // extra fields for storage
                        vendorRefund: '',
                        agencyRefund: '',
                        force_payment_method: forcePaymentMethod || 'none',
                        force_payment_method_note: '',
                        force_payment_method_payment_intent: '',
                        delete_discounts: []
                    });
                    setBookingItemTickets(data.booking_item_tickets || []);
                })
                .catch(err => setError(err))
                .finally(() => setLoading(false));
        },
        [forcePaymentMethod]
    );

    // Reset state when id changes
    useEffect(() => {
        setBooking(null);
        setTicketData(null);
        setError(true);
        setLoading(true);
    }, [id]);

    // on initial render, load the booking item
    useEffect(() => {
        loadBookingItem(id);
    }, [id, loadBookingItem]);

    const onSubmit = async (values, { setSubmitting }) => {
        const updateData = buildUpdateData({
            initialValues: booking,
            values,
            customer
        });

        if (Object.keys(updateData).length === 0) {
            notifySuccess('Nothing to update!');
            return;
        }

        showModal(MODAL_TYPE.CONFIRMATION, {
            title: `Are you sure you want to update this booking item?`,
            content: (
                <div>
                    <h5>List of changes:</h5>
                    <pre
                        style={{
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            maxHeight: '400px',
                            overflow: 'auto'
                        }}
                    >
                        {updateToSimpleText(updateData, booking, values)}
                    </pre>
                </div>
            ),
            confirm: async () => {
                try {
                    setSubmitting(true);
                    await doSwaggerCall('bookings', 'updateBookingItem', {
                        bookingItemId: values.id,
                        updateBookingItemData: updateData
                    });

                    notifySuccess('BookingItem modified!');
                    // this set booking will trigger a form re-init, do NOT remove
                    setBooking(null);
                    loadBookingItem(id);
                } catch (err) {
                    const errorMessage =
                        err?.response?.obj?.message || 'Could not modify bookingItem!';
                    notifyError(errorMessage);
                    // Refresh booking data to ensure UI reflects actual DB state
                    loadBookingItem(id);
                } finally {
                    setSubmitting(false);
                }
            }
        });
    };

    const bookingCanModify = ['unconfirmed', 'confirmed'].includes(booking?.status);

    // Check capability-based permissions for refund and modify actions
    const {
        canRefund,
        canModify,
        refundDisabledReason,
        modifyDisabledReason
    } = useBookingPermissions(booking);

    const transactionSum = booking?.transactions.reduce((sum, transaction) => {
        return (
            sum +
            (transaction.type !== 'refund'
                ? Math.abs(transaction.amount)
                : -1 * Math.abs(transaction.amount))
        );
    }, 0);

    const showRefundModal = useCallback(() => {
        const bookingUserId = booking?.booking?.user?.id;
        showModal(MODAL_TYPE.BOOKING_ITEM_REFUND, {
            bookingUserId,
            transactionSum,
            bookingItemId: id,
            onReload: () => {
                // this set booking will trigger a form re-init, do NOT remove
                setBooking(null);
                loadBookingItem(id);
            }
        });
    }, [booking, id, loadBookingItem, showModal, transactionSum]);

    return (
        <Container flex className="activity-edit page" maxWidth={false} style={{ paddingTop: 0 }}>
            {!loading && error && (
                <Grid
                    container
                    item
                    xs={12}
                    className="mt-2"
                    direction="column"
                    alignItems="center"
                >
                    <Alert severity="error">
                        <h4>{error.response?.body?.message}</h4>
                        {JSON.stringify(error)}
                    </Alert>
                </Grid>
            )}
            {loading && !error && booking === null ? (
                <Grid item xs={12}>
                    <CircularProgress />
                </Grid>
            ) : (
                booking !== null && (
                    <>
                        <Formik onSubmit={onSubmit} initialValues={booking}>
                            {({
                                isSubmitting,
                                values,
                                setFieldValue,
                                errors,
                                dirty,
                                submitForm
                            }) => (
                                <>
                                    <RouterPrompt when={!!dirty} />
                                    <InternalTicketMergingLogic
                                        possibleTickets={ticketData?.possibleTickets}
                                        existingTickets={ticketData?.existingTickets}
                                        values={values}
                                        setFieldValue={setFieldValue}
                                    />
                                    <Form>
                                        {Object.keys(errors).length > 0 && (
                                            <Grid
                                                item
                                                container
                                                spacing={2}
                                                xs={12}
                                                style={{
                                                    color: theme.palette.error.main
                                                }}
                                            >
                                                <Grid item>
                                                    <ErrorIcon color="error" />
                                                </Grid>
                                                <Grid item>
                                                    {Object.values(errors).map(e => (
                                                        <div>{e}</div>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        )}
                                        <Grid container spacing={2}>
                                            <Grid item xs={10}>
                                                <h3 style={{ marginTop: '16px' }}>
                                                    {booking.activity?.name} (
                                                    {booking.activity?.island.name})
                                                </h3>
                                            </Grid>
                                            <Grid container item xs={2} className="mt-2">
                                                <Grid
                                                    container
                                                    xs={12}
                                                    direction="row"
                                                    wrap="nowrap"
                                                    spacing={2}
                                                    justifyContent="flex-end"
                                                    alignItems="center"
                                                >
                                                    <Grid item>
                                                        <DetailsToggleButton
                                                            visibleDetails={visibleDetails}
                                                            setVisibleDetails={setVisibleDetails}
                                                        />
                                                    </Grid>
                                                    {bookingCanModify && (
                                                        <>
                                                            <Grid item>
                                                                <Tooltip
                                                                    title={
                                                                        modifyDisabledReason || ''
                                                                    }
                                                                >
                                                                    <span>
                                                                        <Button
                                                                            size="small"
                                                                            disabled={
                                                                                isSubmitting ||
                                                                                !canModify
                                                                            }
                                                                            variant="contained"
                                                                            onClick={() => {
                                                                                showModal(
                                                                                    MODAL_TYPE.BOOKING_ITEM_TIME,
                                                                                    {
                                                                                        values,
                                                                                        setFieldValue,
                                                                                        bookingCanModify
                                                                                    }
                                                                                );
                                                                            }}
                                                                            style={{
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            Time Change
                                                                        </Button>
                                                                    </span>
                                                                </Tooltip>
                                                            </Grid>
                                                            <Grid item>
                                                                <Tooltip
                                                                    title={
                                                                        refundDisabledReason || ''
                                                                    }
                                                                >
                                                                    <span>
                                                                        <Button
                                                                            size="small"
                                                                            disabled={!canRefund}
                                                                            onClick={
                                                                                showRefundModal
                                                                            }
                                                                            variant="contained"
                                                                            style={{
                                                                                backgroundColor:
                                                                                    theme.palette
                                                                                        .warning
                                                                                        .main,
                                                                                color: 'white'
                                                                            }}
                                                                        >
                                                                            Refund
                                                                        </Button>
                                                                    </span>
                                                                </Tooltip>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => history.goBack()}
                                                    >
                                                        Back
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                            <Grid
                                                item
                                                style={{
                                                    width: '400px',
                                                    alignContent: 'flex-start'
                                                }}
                                                spacing={2}
                                                container
                                            >
                                                <BookingItemTimeslotSettings
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    bookingCanModify={bookingCanModify && canModify}
                                                />
                                                <BookingItemGuestSettings
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    bookingCanModify={bookingCanModify && canModify}
                                                />
                                                <BookingItemGenericSettings
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    bookingCanModify={bookingCanModify && canModify}
                                                    agents={booking.agents || []}
                                                />
                                                <AgentNotesSection
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    bookingCanModify={bookingCanModify && canModify}
                                                    user={user}
                                                />
                                            </Grid>
                                            <Grid
                                                item
                                                style={{ flex: 1, alignContent: 'flex-start' }}
                                                container
                                                spacing={2}
                                            >
                                                {!bookingCanModify && (
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning">
                                                            Only unconfirmed or confirmed bookings
                                                            can be modified.
                                                        </Alert>
                                                    </Grid>
                                                )}
                                                {bookingCanModify && !canModify && (
                                                    <Grid item xs={12}>
                                                        <Alert severity="warning">
                                                            {modifyDisabledReason}
                                                        </Alert>
                                                    </Grid>
                                                )}
                                                {visibleDetails.activityInfoShown && (
                                                    <BookingItemCompanySettings values={values} />
                                                )}
                                                <BookingItemTicketSettings
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    bookingItemTickets={bookingItemTickets}
                                                    bookingCanModify={bookingCanModify && canModify}
                                                    setForcePaymentMethod={setForcePaymentMethod}
                                                />
                                                <BookingItemCustomBookingSettings
                                                    values={values}
                                                    setFieldValue={setFieldValue}
                                                    bookingCanModify={bookingCanModify && canModify}
                                                />
                                                {visibleDetails.transactionShown && (
                                                    <BookingItemTransactionHistory
                                                        values={values}
                                                        setFieldValue={setFieldValue}
                                                        transactionSum={transactionSum}
                                                        agents={booking.agents || []}
                                                        canRefund={canRefund}
                                                        canModify={canModify}
                                                    />
                                                )}
                                                {visibleDetails.emailsShown && (
                                                    <BookingItemEmails bookingItemId={id} />
                                                )}
                                                {visibleDetails.historyShown && (
                                                    <BookingItemHistory bookingItemId={id} />
                                                )}
                                                <Grid item xs={12}>
                                                    <Tooltip
                                                        title={
                                                            !canModify
                                                                ? modifyDisabledReason || ''
                                                                : ''
                                                        }
                                                    >
                                                        <span>
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                disabled={
                                                                    isSubmitting || !canModify
                                                                }
                                                                onClick={submitForm}
                                                                fullWidth
                                                            >
                                                                Save
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                    {isSubmitting && <CircularProgress size={24} />}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                </>
                            )}
                        </Formik>
                    </>
                )
            )}
            <Grid container sx={{ marginTop: 2 }} justifyContent="center"></Grid>
        </Container>
    );
});

export default withTheme(BookingItemEditPage);
